// The billing profile (sculpt-api PLAN-payments.md §3–4): who is billed and where. Sculpt is sold to professionals
// only, so the customer is a company or a sole trader. The country and the VAT number set the VAT of every order;
// the API checks a new VAT number with VIES and answers its status. Admins edit, members read.
// The VAT number is checked with VIES as it is typed (PLAN-payments.md P13): a number VIES rejects cannot be saved,
// nor can a business elsewhere in the EU go on without one VIES confirms.
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { date } from './format';
import { countryOptions, inOtherMemberState, isSiren, needsSiren, sirenOfSiret } from './countries';
import { Alert, Badge, Button, Card, Field, Label, Select } from './ui';
import { ApiError, api } from '../services/api';
import type { CustomerKind, Organization, VatCheck } from '../types';

/** The API's refusals of a profile value (sculpt/billing/profile.py `ProfileError`, answered as 422). */
export const PROFILE_ERRORS = [
  'invalid_country',
  'invalid_customer_kind',
  'invalid_siren',
  'invalid_vat_number',
  'vat_country_mismatch',
  'vat_number_required',
  'vat_number_invalid',
  'vies_unavailable',
];

/** How long typing pauses before the number is sent to VIES. */
export const VAT_CHECK_DELAY_MS = 800;

/** A VAT number as the API stores it: upper case, without spaces, dots or dashes. */
export const normalizeVat = (raw: string) => raw.toUpperCase().replace(/[\s.\-]/g, '');

type LiveVat = { key: string; result: VatCheck | 'checking' | 'failed' };

/** Where the billing page links to complete the profile. */
export const PROFILE_ANCHOR = 'billing-profile';

function formOf(org: Organization) {
  return {
    customer_kind: (org.customer_kind ?? 'company') as CustomerKind,
    billing_name: org.billing_name ?? '',
    billing_street: org.billing_street ?? '',
    billing_zip: org.billing_zip ?? '',
    billing_city: org.billing_city ?? '',
    billing_country: org.billing_country ?? 'FR',
    siren: org.siren ?? '',
    registration_number: org.registration_number ?? '',
    vat_number: org.vat_number ?? '',
  };
}

export function BillingProfileCard({ org, admin, onSaved }: { org: Organization; admin: boolean; onSaved: (o: Organization) => void }) {
  const { t } = useTranslation();
  return (
    <div id={PROFILE_ANCHOR}>
      <Card title={t('profile.title')} actions={org.billing_complete ? <Badge tone="accent">{t('profile.complete')}</Badge> : <Badge tone="warn">{t('profile.incompleteBadge')}</Badge>}>
        <BillingProfileForm org={org} admin={admin} onSaved={onSaved} />
      </Card>
    </div>
  );
}

/** The profile's fields. On its own (`formId` set: the checkout) it has no hint, no warning and no button of its
 *  own: a `<button type="submit" form={formId}>` elsewhere saves it, `onBusy` says while it saves and `onBlocked`
 *  while the VAT number keeps it from being saved (being checked, rejected, or missing where it is required). */
export function BillingProfileForm({ org, admin, onSaved, formId, onBusy, onBlocked }: {
  org: Organization;
  admin: boolean;
  onSaved: (o: Organization) => void;
  formId?: string;
  onBusy?: (busy: boolean) => void;
  onBlocked?: (blocked: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const bare = formId !== undefined;
  const [form, setForm] = useState(() => formOf(org));
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string | null>(null);
  const countries = useMemo(() => countryOptions(i18n.language), [i18n.language]);
  const siren = needsSiren(form.billing_country);
  const set = (patch: Partial<typeof form>) => {
    setForm({ ...form, ...patch });
    setState('idle');
    setError(null);
  };

  // The live VIES check: of the number, country and postal code typed (the code picks a territory, e.g. the Canaries).
  const vatRequired = inOtherMemberState(form.billing_country);
  const number = normalizeVat(form.vat_number);
  const key = `${number}|${form.billing_country}|${form.billing_zip.trim()}`;
  const savedValid = org.vat_status === 'valid' && number === normalizeVat(org.vat_number ?? '') && form.billing_country === org.billing_country;
  const [live, setLive] = useState<LiveVat | null>(null);
  const asked = useRef<string | null>(null);
  const runCheck = useCallback(
    async (k: string) => {
      const [n, country, zip] = k.split('|');
      asked.current = k;
      setLive({ key: k, result: 'checking' });
      try {
        const result = await api.vatCheck(n, country, zip);
        if (asked.current === k) setLive({ key: k, result });
      } catch (err) {
        if (asked.current === k) {
          setLive({ key: k, result: 'failed' });
          if (err instanceof ApiError && err.code === 'rate_limited') setError(t('profile.error.rate_limited'));
        }
      }
    },
    [t],
  );
  const needsCheck = admin && number !== '' && !savedValid;
  useEffect(() => {
    if (!needsCheck || asked.current === key) return;
    const timer = setTimeout(() => void runCheck(key), VAT_CHECK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [needsCheck, key, runCheck]);
  const checkNow = () => {
    if (needsCheck && asked.current !== key) void runCheck(key);
  };
  const current = live && live.key === key ? live.result : null;
  const vatBlocking = admin && (number === '' ? vatRequired : !savedValid && (current === null || current === 'checking' || current === 'failed' || current.blocking));
  useEffect(() => onBlocked?.(vatBlocking), [vatBlocking, onBlocked]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (siren && !isSiren(form.siren)) {
      setError(t('profile.error.sirenRequired'));
      return;
    }
    if (number === '' && vatRequired) {
      setError(t('profile.error.vat_number_required'));
      return;
    }
    if (vatBlocking) {
      checkNow();
      return;
    }
    setState('saving');
    onBusy?.(true);
    try {
      const saved = await api.patchOrganization({
        customer_kind: form.customer_kind,
        billing_name: form.billing_name.trim() || null,
        billing_street: form.billing_street.trim() || null,
        billing_zip: form.billing_zip.trim() || null,
        billing_city: form.billing_city.trim() || null,
        billing_country: form.billing_country,
        siren: siren ? form.siren.trim() : null,
        registration_number: siren ? null : form.registration_number.trim() || null,
        vat_number: form.vat_number.trim() || null,
      });
      setForm(formOf(saved));
      setState('saved');
      onBusy?.(false);
      onSaved(saved);
    } catch (err) {
      setState('idle');
      onBusy?.(false);
      setError(err instanceof ApiError && PROFILE_ERRORS.includes(err.code) ? t(`profile.error.${err.code}`) : t('app.error'));
    }
  };

  return (
    <form id={formId} onSubmit={submit} className="flex flex-col gap-3" data-testid="billing-profile">
      {!bare && <p className="text-[13px] text-muted">{t('profile.hint')}</p>}
      {!bare && !org.billing_complete && <Alert kind="warn">{t('profile.incomplete')}</Alert>}
      <fieldset className="flex flex-col gap-1.5" disabled={!admin}>
        <Label>{t('profile.kind')}</Label>
        <div className="flex flex-wrap gap-4 text-[13px] text-ink">
          {(['company', 'individual'] as const).map((kind) => (
            <label key={kind} className="flex items-center gap-2">
              <input type="radio" name="customer_kind" value={kind} checked={form.customer_kind === kind} onChange={() => set({ customer_kind: kind })} className="text-accent focus:ring-0" />
              {t(`profile.kinds.${kind}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <Field
        id="billing-name"
        label={form.customer_kind === 'company' ? t('profile.nameCompany') : t('profile.nameIndividual')}
        value={form.billing_name}
        onChange={(e) => set({ billing_name: e.target.value })}
        disabled={!admin}
        required
      />
      <Field id="billing-street" label={t('profile.street')} value={form.billing_street} onChange={(e) => set({ billing_street: e.target.value })} disabled={!admin} required />
      <div className="grid gap-3 sm:grid-cols-3">
        <Field id="billing-zip" label={t('profile.zip')} value={form.billing_zip} onChange={(e) => set({ billing_zip: e.target.value })} disabled={!admin} required />
        <Field id="billing-city" label={t('profile.city')} value={form.billing_city} onChange={(e) => set({ billing_city: e.target.value })} disabled={!admin} required />
        <Select id="billing-country" label={t('profile.country')} value={form.billing_country} onChange={(e) => set({ billing_country: e.target.value })} disabled={!admin}>
          {countries.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </Select>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {siren && (
          <Field
            id="siren"
            label={t('profile.siren')}
            hint={t('profile.sirenHint')}
            info={t('profile.sirenInfo')}
            inputMode="numeric"
            value={form.siren}
            onChange={(e) => set({ siren: sirenOfSiret(e.target.value) })}
            disabled={!admin}
            required
          />
        )}
        {!siren && (
          <Field
            id="registration-number"
            label={t('profile.registration')}
            hint={t('profile.registrationHint')}
            info={t('profile.registrationInfo')}
            maxLength={40}
            value={form.registration_number}
            onChange={(e) => set({ registration_number: e.target.value })}
            disabled={!admin}
          />
        )}
        <Field
          id="vat-number"
          label={t('profile.vat')}
          hint={vatRequired ? t('profile.vatHintRequired') : t('profile.vatHint')}
          value={form.vat_number}
          onChange={(e) => set({ vat_number: e.target.value })}
          onBlur={checkNow}
          disabled={!admin}
          required={vatRequired}
          aria-invalid={current !== null && typeof current === 'object' && current.blocking ? true : undefined}
          aria-describedby="vat-live"
        />
      </div>
      <div id="vat-live" aria-live="polite">
        {current !== null && !savedValid ? (
          <LiveVatStatus result={current} required={vatRequired} onRetry={() => void runCheck(key)} />
        ) : number === normalizeVat(org.vat_number ?? '') && form.billing_country === org.billing_country ? (
          <VatStatus org={org} locale={i18n.language} />
        ) : null}
      </div>
      {!admin && <p className="text-[12px] text-faint">{t('org.readOnly')}</p>}
      {!bare && state === 'saved' && <Alert kind="success">{t('app.saved')}</Alert>}
      {error && <Alert kind="error">{error}</Alert>}
      {admin && !bare && (
        <div>
          <Button type="submit" disabled={state === 'saving' || vatBlocking}>
            {state === 'saving' ? t('app.saving') : t('app.save')}
          </Button>
        </div>
      )}
    </form>
  );
}

/** What VIES says of the number being typed. */
function LiveVatStatus({ result, required, onRetry }: { result: VatCheck | 'checking' | 'failed'; required: boolean; onRetry: () => void }) {
  const { t } = useTranslation();
  const retry = (
    <Button variant="secondary" className="ml-2 py-1!" onClick={onRetry}>
      {t('profile.vatRetry')}
    </Button>
  );
  if (result === 'checking')
    return (
      <p className="flex items-center gap-2 text-[12.5px] text-muted" data-testid="vat-checking">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-line-btn border-t-accent" aria-hidden />
        {t('profile.vatChecking')}
      </p>
    );
  if (result === 'failed')
    return (
      <Alert kind="error">
        {t('profile.vatCheckFailed')}
        {retry}
      </Alert>
    );
  if (result.status === 'valid') {
    const name = result.name && result.name.replace(/-/g, '').trim() ? result.name : null;
    return <Alert kind="success">✓ {name ? t('profile.vatRecognized', { name }) : t('profile.vatRecognizedNoName')}</Alert>;
  }
  if (result.reason === 'invalid_vat_number' || result.reason === 'vat_country_mismatch') return <Alert kind="error">{t(`profile.error.${result.reason}`)}</Alert>;
  if (result.status === 'invalid') return <Alert kind="error">✗ {t('profile.vatRejected')}</Alert>;
  if (result.status === 'unavailable')
    return result.blocking || required ? (
      <Alert kind="warn">
        {t('profile.vatUnavailableEu')}
        {retry}
      </Alert>
    ) : (
      <Alert kind="warn">{t('profile.vatUnavailableFr')}</Alert>
    );
  return null;
}

/** What VIES said of the saved VAT number. */
function VatStatus({ org, locale }: { org: Organization; locale: string }) {
  const { t } = useTranslation();
  if (!org.vat_number) return null;
  if (org.vat_status === 'valid') return <Alert kind="success">{t('profile.vatValid', { number: org.vat_number, date: date(org.vat_checked_at, locale) })}</Alert>;
  if (org.vat_status === 'invalid') return <Alert kind="error">{t('profile.vatInvalid', { number: org.vat_number })}</Alert>;
  if (org.vat_status === 'review')
    return (
      <Alert kind="warn">
        {t('profile.vatReview', { number: org.vat_number })}
        {inOtherMemberState(org.billing_country) ? ` ${t('profile.vatReviewHeld')}` : ''}
      </Alert>
    );
  return null;
}
